// Client-side, this document now serves only the Visibility Checker. The client visibility report's PDF
// is rendered by the backend from a copy (apps/backend/src/shared/visibility-report-pdf/) — a change
// here (layout, texts, the four charts) has to be made there too.
import React from 'react';
import {
  Defs,
  Circle,
  Document,
  G,
  Path,
  Image,
  Link,
  LinearGradient,
  Page,
  RadialGradient,
  Rect,
  StyleSheet,
  Stop,
  Svg,
  Text,
  View,
} from '@react-pdf/renderer';
import type {
  PublicScanReportResponse,
  VisibilityReportBrandPresence,
  VisibilityReportContextGap,
  VisibilityReportFunnel,
  VisibilityReportTopicShare,
} from '@brand24-original/llm-types';
import { registerPdfFonts } from '../../../services/pdf/fonts/registerFonts';
import chatbeatLogoFull from '../../../services/pdf/assets/chatbeat-logo-full';
import { SOV_PIE_CHART_PALETTE } from '../../../utils/sovPalette';
import { formatCompactCount, formatCount } from '../../../utils/utils';
import { brandLabel } from '../brandLabel';
import { PDF_COLORS, PDF_GRADIENT_HEIGHT, PDF_PAGE } from './reportPdfTheme';
import { MODEL_ICON_SHAPES } from './modelIconShapes';
import {
  PDF_INNER_WIDTH,
  PDF_INNER_WIDTH_LANDSCAPE,
  ReportPdfBar,
  ReportPdfPanel,
  ReportPdfPanelHeading,
  ReportPdfSection,
} from './ReportPdfPrimitives';

registerPdfFonts();

export interface ReportPdfLabels {
  coverSubtitle: string;
  /**
   * Podpis pod tytułem okładki: zakres raportu, a dla Checkera (brak okresu) data wygenerowania —
   * dokładnie to, co pokazuje `ReportCover` na ekranie. PDF drukował tu zawsze datę wygenerowania,
   * więc pełny raport miał na okładce inną datę niż w podglądzie.
   */
  coverDate: string;
  prompts: string;
  models: string;
  /** Podpis kafla z liczbą odpowiedzi; kafel pojawia się tylko z `responsesAnalyzed`. */
  allResponses: string;
  summaryTitle: string;
  summaryTitleAccent: string;
  summarySubtitle: string;
  visibility: string;
  /** Gotowa wartość kafla widoczności — bez `NEW_STATS` to etykieta opisowa, nie procent. */
  visibilityValue: string;
  shareOfVoice: string;
  brandScore: string;
  /** Etykieta i wartość kafla pozycji: średnia albo mediana, zależnie od wersji statystyk konta. */
  positionLabel: string;
  positionValue: string;
  platformTitle: string;
  platformTitleAccent: string;
  platformDominant: string;
  platformWeakest: string;
  platformEven: string;
  platformEvenDesc: (count: number) => string;
  platformShare: (value: number) => string;
  platformMetricsTitle: string;
  platformMetricsDesc: string;
  rankingTitle: string;
  rankingTitleAccent: string;
  rankingSubtitle: string;
  sovTitle: string;
  sovTitleAccent: string;
  sovSubtitle?: string;
  sovChartTitle: string;
  sovChartDesc: string;
  matrixTitle: string;
  matrixTitleAccent: string;
  matrixSubtitle: string;
  gapsTitle: string;
  gapsTitleAccent: string;
  gapsSubtitle: string;
  gapsTopic: string;
  gapsBrands: string;
  gapsOccurrences: string;
  domainsTitle: string;
  domainsTitleAccent: string;
  domainsSubtitle: string;
  domainsShare: (value: number) => string;
  pagesTitle: string;
  pagesTitleAccent: string;
  pagesSubtitle: string;
  promptsTitle: string;
  promptsTitleAccent: string;
  promptsSubtitle: string;
  promptsTopTitle: string;
  promptsTopDesc: string;
  /** Nagłówek panelu, gdy w raporcie są grupy promptów — lista przestaje być samymi pytaniami. */
  promptsTopTitleGroups: string;
  promptsTopDescGroups: string;
  /** Tag wiersza grupy: krótki na liście, z liczbą promptów w kaflu skrajnym. */
  promptsGroupTag: (count: number) => string;
  promptsGroupTagCount: (count: number) => string;
  /** Podpis wydzielonego wiersza z najsłabszym promptem. */
  promptsLowest: string;
  promptsBest: string;
  promptsWorst: string;
  promptsEven: string;
  promptsEvenDesc: (count: number) => string;
  brand: string;
  trendLegend: { label: string; color: string }[];
  trendTitle: string;
  trendTitleAccent: string;
  trendSubtitle: string;
  funnelTitle: string;
  funnelTitleAccent: string;
  funnelSubtitle: string;
  funnelMentioned: string;
  funnelTop3: string;
  funnelTop1: string;
  funnelMentionedDesc: (responses: string, total: string) => string;
  funnelTop3Desc: (responses: string) => string;
  funnelTop1Desc: (responses: string) => string;
  funnelStepTop3: string;
  funnelStepTop3Desc: (value: number) => string;
  funnelStepTop1: string;
  funnelStepTop1Desc: (value: number) => string;
  topicsTitle: string;
  topicsTitleAccent: string;
  topicsSubtitle: string;
  presenceTitle: string;
  presenceTitleAccent: string;
  presenceSubtitle: string;
  rankingYourBrand: string;
  /** Separator tysięcy zgodny z językiem interfejsu — react-pdf nie widzi kontekstu i18n. */
  numberFormat: (value: number) => string;
  promptsLockedTitle: string;
  promptsLockedDesc: string;
  promptsLockedCta: string;
  /** Bezwzględny adres rejestracji — w PDF-ie link musi być pełny, bo nie ma origin-u strony. */
  signUpUrl: string;
}

interface ReportPdfDocumentProps {
  report: PublicScanReportResponse;
  labels: ReportPdfLabels;
  /** Nazwa modelu → etykieta; front trzyma ją w i18n, dokument dostaje gotową funkcję. */
  modelName: (model: string) => string;
  /** Zrzuty wykresów ECharts ze strony (data URI) — wchodzą jako obraz, bo react-pdf ich nie odtworzy. */
  rankingChart?: string;
  sovChart?: string;
  trendChart?: string;
  topicShareChart?: string;
  /** Luki kontekstowe z insightów; pusta lista = slajdu nie ma (jak na ekranie). */
  contextGaps?: VisibilityReportContextGap[];
  /** Liczba odpowiedzi objętych raportem — trzeci kafel okładki (Checker go nie ma). */
  responsesAnalyzed?: number;
  /** Sekcje spoza kontraktu Checkera — pełny raport klienta dokłada je z własnego payloadu. */
  funnel?: VisibilityReportFunnel;
  topicShares?: VisibilityReportTopicShare[];
  brandPresence?: VisibilityReportBrandPresence;
}

/**
 * Szerokości pasków liczone z geometrii strony, nie na oko:
 *   treść sekcji      = 523 (A4 - marginesy) - 36 (padding sekcji) = 487
 *   kolumna `aside`   = 487 - 185 (38% na `main`) - 20 (odstęp) = 282
 *   wnętrze panelu    = 282 - 28 (padding panelu) = 254
 *   pasek             = 254 - 70 (nazwa) - 30 (wartość) - 16 (dwa odstępy) = 138
 *
 * Za szeroki pasek wypychał kolumnę wartości i „100%" łamało się na „100-" + „%".
 */
const ASIDE_BAR_WIDTH = 138;
/** Odstęp między kaflami w siatce domen/stron. */
const CARD_GAP = 7;
/** Slajd bez panelu — pasek dostaje całą szerokość treści: 487 - 70 - 30 - 16. */
const RANKING_BAR_WIDTH = 371;
/** Długości pasków w szkielecie zamkniętej sekcji — malejące, żeby przypominał prawdziwy ranking. */
const LOCKED_TEASER_WIDTHS = [92, 78, 71, 64, 55, 47];
/** Ile pozycji ma ranking promptów — tak samo jak `TOP_PROMPTS` na slajdzie. */
const PDF_TOP_PROMPTS = 10;
/** Ile znaków nazwy marki mieści się w kaflu „Your brand" obok procentu. */
const RANKING_BRAND_CHARS = 22;
/** Pasek w kafelku źródła: na stronie ma 6px, czyli 4.5pt — sekcyjne 10pt wyglądały tu jak walec. */
const CARD_BAR_HEIGHT = 4.5;
/**
 * Wysokości pasków przeliczone ze skali dokumentu (523pt treści na 1080px widoku ≈ 0.485), a nie
 * przyjęte na oko: 20px paska platform → 8pt (pasek w PDF jest węższy, więc pełne 9,7pt wyglądało
 * masywniej niż w panelu), 10px paska promptów → 5pt. Wcześniej oba brały domyślne 10pt i sekcja
 * promptów miała paski dwa razy grubsze niż na ekranie.
 */
const PLATFORM_BAR_HEIGHT = 8;
const PROMPT_BAR_HEIGHT = 5;
/**
 * Folder z tagu grupy — ta sama ścieżka co `assets/icons/ico-folder.svg` (viewBox 18×18). Na stronie
 * ikona jest maską CSS, a react-pdf mask-image nie renderuje, więc rysujemy ją wprost.
 */
const FOLDER_PATH =
  'M16.5 14.25C16.5 14.6478 16.342 15.0294 16.0607 15.3107C15.7794 15.592 15.3978 15.75 15 15.75H3C2.60218 15.75 2.22064 15.592 1.93934 15.3107C1.65804 15.0294 1.5 14.6478 1.5 14.25V3.75C1.5 3.35218 1.65804 2.97064 1.93934 2.68934C2.22064 2.40804 2.60218 2.25 3 2.25H6.75L8.25 4.5H15C15.3978 4.5 15.7794 4.65804 16.0607 4.93934C16.342 5.22064 16.5 5.60218 16.5 6V14.25Z';
/** 14px ikony (`ico--xxs`) w skali dokumentu; przy 5,5pt tekstu tagu to ta sama proporcja co na stronie. */
const GROUP_TAG_ICON = 7;
/** Tyle żetonów marek mieści się w wierszu luki kontekstowej — reszta chowa się pod „+N", jak na stronie. */
const GAP_VISIBLE_BRANDS = 5;
/** `tableRow.paddingHorizontal` — szerokości kolumn muszą je odjąć, inaczej suma przekracza wiersz. */
const TABLE_ROW_PADDING_X = 4;
/** Kolumna opisowa tabel (marka w macierzy, temat w obecności marek). */
const TABLE_LABEL_WIDTH = 110;
/** Nazwa marki w nagłówku kolumny: 3 linie po ~8 znaków przy 41 pt szerokości. */
const PRESENCE_HEAD_CHARS = 24;
/** Wiersz promptu ma numer zamiast nazwy, a treść stoi NAD paskiem: 254 - 16 - 30 - 16. */
const PROMPT_BAR_WIDTH = 192;

const styles = StyleSheet.create({
  page: {
    fontFamily: 'Inter',
    fontSize: 9,
    color: PDF_COLORS.grey900,
    backgroundColor: PDF_COLORS.grey10,
    paddingTop: PDF_PAGE.margin,
    paddingBottom: PDF_PAGE.margin,
    paddingHorizontal: PDF_PAGE.margin,
  },
  gradient: { position: 'absolute', top: -PDF_PAGE.margin, left: -PDF_PAGE.margin },

  cover: { marginBottom: 16 },
  logo: { width: 104, height: 20, marginBottom: 24 },
  coverBrand: { fontSize: 13, fontWeight: 'normal', color: PDF_COLORS.primary },
  coverTitle: {
    marginTop: 2,
    fontSize: 29,
    fontWeight: 'bold',
    color: PDF_COLORS.grey900,
    lineHeight: 1.05,
  },
  coverRule: {
    width: 23,
    height: 1.5,
    marginVertical: 12,
    borderRadius: 1,
    backgroundColor: PDF_COLORS.primary,
  },
  coverDate: { fontSize: 8, color: PDF_COLORS.grey600 },
  coverStats: { flexDirection: 'row', gap: 8, marginTop: 16 },
  // Kafel jak na stronie: liczba po lewej, lista modeli dosunięta do prawej krawędzi.
  coverStat: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    padding: 12,
    borderRadius: 8,
    backgroundColor: PDF_COLORS.white,
  },
  coverStatValue: { fontSize: 15, fontWeight: 'bold', color: PDF_COLORS.primary },
  coverStatLabel: { marginTop: 3, fontSize: 7, color: PDF_COLORS.grey500 },
  coverStatIcons: { flexDirection: 'row', alignItems: 'center', gap: 6 },

  tiles: { flexDirection: 'row', gap: 8 },
  tile: { flex: 1, padding: 12, borderRadius: 8, backgroundColor: PDF_COLORS.grey50 },
  tileValue: { fontSize: 17, fontWeight: 'bold', color: PDF_COLORS.grey900 },
  tileLabel: { marginTop: 4, fontSize: 8, color: PDF_COLORS.grey600 },

  // Kafle „Dominant / Weakest" — sam pasek z lewej, tak jak na stronie.
  edgeBox: { paddingLeft: 10, borderLeftWidth: 3, borderLeftColor: PDF_COLORS.grey300 },
  edgeBoxTop: { borderLeftColor: PDF_COLORS.secondary },
  edgeLabel: { fontSize: 8, fontWeight: 'bold', color: PDF_COLORS.grey500 },
  edgeName: { marginTop: 5, fontSize: 15, fontWeight: 'bold', color: PDF_COLORS.grey900 },
  edgeShare: { marginTop: 3, fontSize: 8, fontWeight: 'bold', color: PDF_COLORS.grey600 },

  barRow: { flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 7 },
  barName: { width: 70, fontSize: 8, fontWeight: 'bold', textAlign: 'right' },
  barValue: { width: 30, fontSize: 8, fontWeight: 'bold' },
  scale: { flexDirection: 'row', marginTop: 6, paddingLeft: 78 },
  scaleTicks: { flexDirection: 'row', justifyContent: 'space-between' },
  scaleTick: { fontSize: 7, color: PDF_COLORS.grey500 },
  chartImage: { width: '100%', objectFit: 'contain' },

  // Lejek — trzy kafle w rzędzie, pod nimi dwa. Proporcje jak na stronie, przeskalowane do punktów.
  funnelStages: { flexDirection: 'row', gap: 8 },
  funnelStage: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
    padding: 12,
    borderRadius: 8,
  },
  funnelStagePrimary: { backgroundColor: PDF_COLORS.primary100 },
  funnelStageSecondary: { backgroundColor: PDF_COLORS.secondary100 },
  funnelStageBrand: { backgroundColor: PDF_COLORS.secondary },
  funnelStageTitle: { fontSize: 8, fontWeight: 'bold', color: PDF_COLORS.grey700, textAlign: 'center' },
  funnelStagePercent: { fontSize: 22, fontWeight: 'bold', color: PDF_COLORS.grey900 },
  funnelStageDesc: { fontSize: 7, color: PDF_COLORS.grey600, textAlign: 'center' },
  funnelStageTitleOnBrand: { color: PDF_COLORS.white },
  funnelStagePercentOnBrand: { color: PDF_COLORS.white },
  funnelStageDescOnBrand: { color: PDF_COLORS.white },
  funnelSteps: { flexDirection: 'row', gap: 8, marginTop: 8 },
  funnelStep: { flex: 1, gap: 3, padding: 10, borderRadius: 8, backgroundColor: PDF_COLORS.grey50 },
  funnelStepTitle: { fontSize: 9, fontWeight: 'bold' },
  funnelStepTitlePrimary: { color: PDF_COLORS.primary },
  funnelStepTitleBrand: { color: PDF_COLORS.secondary },
  funnelStepDesc: { fontSize: 8, color: PDF_COLORS.grey600 },

  // Żeton marki = `.tag.tag--brand` w komórce tabeli (`table.scss`): szare tło `--grey-ux-100`,
  // BEZ obwódki, tekst w `--primary-500`, pełne zaokrąglenie. Odstęp jak `.tags--standard-gap`
  // (7px → 5pt). Wymiary przeliczone z 12px/4px/10px na punkty.
  tags: { flexDirection: 'row', flexWrap: 'wrap', gap: 5 },
  tag: {
    backgroundColor: PDF_COLORS.grey100,
    borderRadius: 90,
    paddingVertical: 3,
    paddingHorizontal: 7,
    fontSize: 8,
    fontWeight: 'bold',
    color: PDF_COLORS.primaryText,
  },
  // „+N" to na stronie `.more-models`: prostokąt z obwódką `--grey-ux-200`, nie pigułka.
  tagMore: {
    backgroundColor: PDF_COLORS.white,
    borderWidth: 1,
    borderColor: PDF_COLORS.grey200,
    borderRadius: 4,
    paddingVertical: 2,
    paddingHorizontal: 4,
    fontSize: 7,
    fontWeight: 'normal',
    color: PDF_COLORS.grey700,
  },

  // Wartości pod zrzutem wykresu — PDF nie ma tooltipów, więc liczby muszą być wypisane.
  chartValues: { flexDirection: 'row', flexWrap: 'wrap', gap: 4, marginTop: 8 },
  chartValue: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingVertical: 2,
    paddingHorizontal: 6,
    borderRadius: 90,
    backgroundColor: PDF_COLORS.grey50,
  },
  chartValueDot: { width: 5, height: 5, borderRadius: 3 },
  chartValueLabel: { fontSize: 7, color: PDF_COLORS.grey700 },
  chartValueNumber: { fontSize: 7, fontWeight: 'bold', color: PDF_COLORS.grey900 },

  // Legenda nad zrzutem wykresu — zrzut ECharts nie niesie legendy, bo `LineChart` rysuje ją w DOM-ie.
  chartLegend: { flexDirection: 'row', flexWrap: 'wrap', gap: 12, marginBottom: 8 },
  chartLegendItem: { flexDirection: 'row', alignItems: 'center', gap: 4 },
  chartLegendDot: { width: 6, height: 6, borderRadius: 3 },
  chartLegendLabel: { fontSize: 8, color: PDF_COLORS.grey700 },

  // Darmowy Checker: zamiast treści promptów szkielet (paski bez tekstu) i kafel z zachętą.
  // react-pdf nie zna `filter: blur`, więc odpowiednikiem rozmycia jest brak jakiejkolwiek treści.
  lockedRow: { flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 7 },
  lockedTextBar: { height: 6, borderRadius: 3, backgroundColor: PDF_COLORS.grey60, marginBottom: 5 },
  lockedCta: {
    alignItems: 'center',
    gap: 5,
    marginTop: 14,
    padding: 16,
    borderRadius: 8,
    backgroundColor: PDF_COLORS.primary100,
  },
  lockedCtaTitle: { fontSize: 11, fontWeight: 'bold', color: PDF_COLORS.grey900 },
  lockedCtaDesc: { fontSize: 8, color: PDF_COLORS.grey600, textAlign: 'center' },
  lockedCtaLink: { fontSize: 9, fontWeight: 'bold', color: PDF_COLORS.primary, textDecoration: 'none' },

  // Kafel „Your brand" w rankingu — odpowiednik zielonego boxa ze strony.
  rankingBrand: { gap: 4, padding: 12, borderRadius: 8, marginBottom: 10 },
  rankingBrandLabel: { fontSize: 8, color: PDF_COLORS.white },
  // Nazwa do lewej, procent do prawej — jak na stronie. react-pdf nie ma `text-overflow`, więc
  // nazwę przycinamy w kodzie (`truncate`), inaczej długa marka wypchnęłaby procent poza kafel.
  rankingBrandValue: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    gap: 8,
  },
  rankingBrandName: { fontSize: 14, fontWeight: 'bold', color: PDF_COLORS.white },
  rankingBrandPercent: { fontSize: 14, fontWeight: 'bold', color: PDF_COLORS.white },

  rankRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 4,
    paddingVertical: 4,
    paddingHorizontal: 6,
  },
  rankRowTracked: { backgroundColor: PDF_COLORS.primary100, borderRadius: 6 },
  rankDot: { width: 7, height: 7, borderRadius: 2 },
  rankLabel: { flex: 1, fontSize: 9 },
  rankValue: { fontSize: 9, fontWeight: 'bold' },

  table: {},
  tableRow: {
    flexDirection: 'row',
    alignItems: 'center',
    borderBottomWidth: 1,
    borderColor: PDF_COLORS.stroke,
    paddingVertical: 5,
    paddingHorizontal: 4,
  },
  tableRowTracked: { backgroundColor: PDF_COLORS.primary100, borderRadius: 4 },
  // Wiersze, których wysokość robią zawijane pigułki: opis i liczba trzymają GÓRNĄ krawędź, bo
  // wyśrodkowane pionowo odklejały się od pierwszego rzędu pigułek.
  tableRowTop: { alignItems: 'flex-start' },
  tableHead: { fontSize: 7, fontWeight: 'bold', color: PDF_COLORS.grey500, textAlign: 'center' },
  tableHeadTracked: { color: PDF_COLORS.primaryText },
  tableBrandCell: { width: TABLE_LABEL_WIDTH, flexDirection: 'row', alignItems: 'center', gap: 5 },
  tableBrand: { fontSize: 8, fontWeight: 'bold', color: PDF_COLORS.grey800 },
  // Zaślepka marki — odpowiednik `BrandIcon` ze strony.
  brandBadge: {
    width: 14,
    height: 14,
    borderRadius: 4,
    color: PDF_COLORS.white,
    fontSize: 7,
    fontWeight: 'bold',
    textAlign: 'center',
    paddingTop: 3.5,
  },
  // Kolorowana jest sama wartość, nie komórka — stąd własne zaokrąglenie, jak na stronie.
  tableCellWrap: { alignItems: 'center' },
  tableCellTracked: { backgroundColor: PDF_COLORS.primary100, borderRadius: 4 },
  tableCell: {
    paddingVertical: 3,
    paddingHorizontal: 6,
    borderRadius: 8,
    fontSize: 8,
    fontWeight: 'bold',
    textAlign: 'center',
  },

  // Trzy kolumny. Szerokość w procentach, nie w punktach — przy podziale co do punktu zaokrąglenia
  // wypychały trzecią kartę do następnego wiersza. 3 × 32% zostawia margines błędu na odstępy.
  grid: { flexDirection: 'row', flexWrap: 'wrap' },
  card: {
    width: '32%',
    marginRight: CARD_GAP,
    marginBottom: CARD_GAP,
    flexDirection: 'row',
    alignItems: 'center',
    padding: 8,
    borderWidth: 1,
    borderColor: PDF_COLORS.stroke,
    borderRadius: 8,
  },
  cardLast: { marginRight: 0 },
  cardTracked: { borderColor: PDF_COLORS.primary100, backgroundColor: PDF_COLORS.primary100 },
  cardRank: {
    width: 16,
    height: 16,
    marginRight: 7,
    borderRadius: 4,
    backgroundColor: PDF_COLORS.grey60,
    fontSize: 7,
    fontWeight: 'bold',
    color: PDF_COLORS.grey600,
    textAlign: 'center',
    paddingTop: 4,
  },
  cardMain: { flex: 1 },
  cardName: {
    fontSize: 8,
    fontWeight: 'bold',
    color: PDF_COLORS.grey900,
  },
  cardMeta: {
    marginTop: 2,
    marginBottom: 4,
    fontSize: 6,
    color: PDF_COLORS.grey600,
  },
  // Link dziedziczy z react-pdf niebieski kolor i podkreślenie — kasujemy je, kafel ma wyglądać jak wcześniej.
  cardNameLink: {
    fontSize: 8,
    fontWeight: 'bold',
    color: PDF_COLORS.grey900,
    textDecoration: 'none',
  },
  cardMetaLink: {
    marginTop: 2,
    marginBottom: 4,
    fontSize: 6,
    color: PDF_COLORS.grey600,
    textDecoration: 'none',
  },
  cardCitations: { fontSize: 11, fontWeight: 'bold' },

  extreme: { flexDirection: 'row', gap: 10, padding: 12, borderRadius: 8, marginBottom: 8 },
  // Komórka rozciąga się na wysokość kafla i centruje zawartość u siebie. `alignItems`/`alignSelf`
  // /`margin: auto` na samym dziecku react-pdf ignoruje — zostawiał ikonę i procent przy górnej
  // krawędzi, na wysokości pierwszej linii etykiety.
  extremeCell: { justifyContent: 'center' },
  // Białe kółko pod ikoną — na barwnym tle kafla „najwyższej" sama ikona ginie.
  // `alignSelf` zamiast polegania na `alignItems` rodzica — react-pdf nie zawsze stosuje je do
  // mieszanki View/Text, przez co kółko i procent lądowały przy górnej krawędzi kafla.
  // Marginesy `auto` w pionie zamiast `alignSelf`/`alignItems` — react-pdf ustawia dziecko przy
  // górnej krawędzi mimo obu, a `auto` rozdziela wolną przestrzeń po równo i centruje naprawdę.
  extremeIcon: { flexShrink: 0 },
  // `flex: 1` jest tu potrzebne: kafel to RZĄD, więc `flexBasis: 0` dotyczy szerokości i pozwala
  // kolumnie oddać miejsce wartości procentowej. Wysokość i tak liczy się z treści.
  extremeContent: { flex: 1 },
  extremeBest: { backgroundColor: PDF_COLORS.secondary100 },
  extremeWorst: { backgroundColor: PDF_COLORS.grey50 },
  extremeLabel: { fontSize: 8, fontWeight: 'bold', marginBottom: 6 },
  // BEZ `flex: 1`: to pozostałość po układzie, w którym prompt i wartość leżały w jednym wierszu.
  // W kolumnie `flex: 1` znaczy `flexBasis: 0`, więc tekst nie wnosił swojej wysokości — kafel
  // zostawał wysokości etykiety, a dłuższy prompt wylewał się na sąsiedni kafel.
  extremePrompt: { fontSize: 9, color: PDF_COLORS.grey900 },
  // Trzecia kolumna kafla (ikona | treść | wartość) — trzyma tę samą oś co ikona.
  extremeValue: { fontSize: 11, fontWeight: 'bold' },

  promptRow: { flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 7 },
  promptLowest: { marginTop: 6, paddingTop: 8, borderTopWidth: 1, borderTopColor: PDF_COLORS.grey100 },
  promptLowestLabel: { flexDirection: 'row', alignItems: 'center', gap: 6, marginBottom: 6 },
  promptLowestGap: {
    width: 16,
    height: 16,
    borderRadius: 4,
    backgroundColor: PDF_COLORS.grey60,
    alignItems: 'center',
    justifyContent: 'center',
    alignSelf: 'center',
  },
  promptLowestText: { fontSize: 8, color: PDF_COLORS.grey600 },
  // Ujemny margines poziomy kompensuje padding, więc kolumny (a z nimi PASEK o sztywnej szerokości
  // PROMPT_BAR_WIDTH) stoją dokładnie tam, co w wierszach listy — inaczej pasek wystawał poza tło.
  promptLowestRow: {
    marginBottom: 0,
    marginHorizontal: -6,
    padding: 6,
    borderRadius: 6,
    backgroundColor: PDF_COLORS.grey10,
  },
  promptRank: {
    width: 16,
    height: 16,
    borderRadius: 4,
    backgroundColor: PDF_COLORS.grey60,
    fontSize: 7,
    fontWeight: 'bold',
    color: PDF_COLORS.grey600,
    textAlign: 'center',
    paddingTop: 4,
  },
  promptMain: { flex: 1 },
  promptText: { fontSize: 8, fontWeight: 'bold', color: PDF_COLORS.grey800, marginBottom: 5 },
  // Tag grupy stoi w wierszu OBOK nazwy, a nie w jej `Text` — react-pdf nie zna `vertical-align`,
  // więc pigułka wstawiona w tekst siadałaby na linii bazowej, poniżej środka wiersza.
  promptLabelRow: { flexDirection: 'row', alignItems: 'center', gap: 6, marginBottom: 5 },
  // Proporcje pigułki przeliczone z `report-prompts__group-tag`: cały dokument stoi na skali ~0,65
  // (12px → 8pt tekstu promptu), więc 8px → 5,5pt, 8px odstępu → 5, 4px zaokrąglenia → 3.
  // `letterSpacing` w react-pdf jest w PUNKTACH, nie w `em` — 0,04em z CSS to tutaj 0,22.
  // Bez wersalika — tag ma zostać w pisowni, którą niesie tłumaczenie.
  promptGroupTag: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 3,
    paddingHorizontal: 5,
    paddingVertical: 2,
    borderRadius: 3,
    backgroundColor: PDF_COLORS.secondary100,
  },
  promptGroupTagText: {
    color: PDF_COLORS.secondary600,
    fontSize: 5.5,
    fontWeight: 'bold',
    letterSpacing: 0.22,
  },
  // W kaflu skrajnym tag stoi nad nazwą grupy, na tle kafla — stąd biała pigułka zamiast zielonej.
  // Szerokość ogranicza mu wiersz-opakowanie, nie `alignSelf` (patrz komentarz przy renderowaniu).
  extremeGroupTagRow: { flexDirection: 'row', marginBottom: 4 },
  extremeGroupTag: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 3,
    paddingHorizontal: 5,
    paddingVertical: 2,
    borderRadius: 3,
    backgroundColor: PDF_COLORS.white,
  },
  extremeGroupTagText: {
    color: PDF_COLORS.grey600,
    fontSize: 5.5,
    fontWeight: 'bold',
    letterSpacing: 0.22,
  },
});

const SCALE_TICKS = [0, 25, 50, 75, 100];

/** Te same progi i barwy co `intensityOf` na stronie; `null` = bez tła. */
const cellStyle = (value: number | null): { backgroundColor?: string; color: string } => {
  if (value === null || value === 0) return { color: PDF_COLORS.grey500 };
  if (value < 10) return { backgroundColor: PDF_COLORS.primary100, color: PDF_COLORS.grey900 };
  if (value < 25) return { backgroundColor: PDF_COLORS.primary200, color: PDF_COLORS.grey900 };
  if (value < 40) return { backgroundColor: PDF_COLORS.primary400, color: PDF_COLORS.white };
  return { backgroundColor: PDF_COLORS.primary600, color: PDF_COLORS.white };
};

/**
 * Przycięcie tekstu do jednej linii. react-pdf nie ma odpowiednika `text-overflow`, a przy zbyt
 * długim wyrazie łamie go dywizem w środku („SCALINGTHEWEBCOM-PREHENSIVE"), więc skracamy przed
 * renderem.
 *
 * Limity wynikają z szerokości kolumny tekstu w kaflu (~97 pt po odjęciu numeru, liczby i paddingu):
 * przy 8 pt mieści się ok. 18 znaków, przy 6 pt — ok. 28.
 */
const truncate = (text: string, maxChars: number): string =>
  text.length > maxChars ? `${text.slice(0, maxChars - 1).trimEnd()}…` : text;

const CARD_TITLE_CHARS = 18;
const CARD_META_CHARS = 28;

/**
 * Adres do pokazania w kaflu: bez protokołu, `www` i parametrów śledzących. Pełny URL nie mieści się
 * w kolumnie, a react-pdf łamie go wtedy dywizami w środku słowa („cyberthreatal-liance").
 */
const shortUrl = (url: string): string => {
  try {
    const parsed = new URL(url);
    const path = parsed.pathname.replace(/\/$/, '');
    return truncate(`${parsed.hostname.replace(/^www\./, '')}${path}`, CARD_META_CHARS);
  } catch {
    return truncate(url, CARD_META_CHARS);
  }
};

/**
 * Wersaliki z rozstrzeleniem, tak jak `text-transform: uppercase` + `letter-spacing` na stronie.
 * react-pdf nie zna żadnej z tych właściwości (sprawdzone w typach i w kodzie pakietu), więc efekt
 * składamy w samym tekście. Zwykła spacja, nie cienka (U+2009) — osadzony Inter nie musi mieć jej
 * glifu, a wtedy litery skleiłyby się z powrotem.
 */
const spacedCaps = (text: string): string => text.toUpperCase().split('').join(' ');

/** Kolor marki — ta sama reguła co w rankingu i na wykresie SoV. */
const brandColor = (index: number, isTracked: boolean): string =>
  isTracked ? PDF_COLORS.secondary : SOV_PIE_CHART_PALETTE[index % SOV_PIE_CHART_PALETTE.length];

/**
 * Raport widoczności jako PDF. Odwzorowuje układ strony wektorowo (żywy tekst, ostre krawędzie),
 * zamiast zrzucać DOM do bitmapy — dlatego kolory i wymiary są tu literalne, ale wzięte z tych
 * samych tokenów co style.
 */
export const ReportPdfDocument: React.FC<ReportPdfDocumentProps> = ({
  report,
  labels,
  modelName,
  rankingChart,
  sovChart,
  trendChart,
  topicShareChart,
  contextGaps = [],
  responsesAnalyzed,
  funnel,
  topicShares = [],
  brandPresence,
}) => {
  const modelValue = (entry: { visibility: number }) => entry.visibility;
  const brandValue = (entry: { visibility: number }) => entry.visibility;

  const models = [...report.visibilityPerModel].sort((a, b) => modelValue(b) - modelValue(a));
  const dominant = models[0];
  const weakest = models.length > 1 ? models[models.length - 1] : null;
  const maxBrandVisibility = Math.max(...report.brandVisibility.map((b) => brandValue(b)), 1);
  // Wiersz tabeli ma poziomy padding, więc dla komórek zostaje mniej niż szerokość panelu.
  // Bez tego suma szerokości przekraczała wiersz i yoga dociskała kolumny.
  const tableContentWidth = (panelWidth: number): number =>
    panelWidth - TABLE_ROW_PADDING_X * 2 - TABLE_LABEL_WIDTH;
  const matrixColumnWidth = report.competitorMatrix.models.length
    ? tableContentWidth(PDF_INNER_WIDTH) / report.competitorMatrix.models.length
    : 0;
  // Obecność marek jest w PDF ODWRÓCONA względem strony: tam tabela przewija się w poziomie, a tu
  // scrolla nie ma. Tematów może być dowolnie dużo, marek najwyżej `VISIBILITY_REPORT_TOP_BRANDS`
  // + marka projektu — więc marki idą w kolumny (policzalne), a tematy w wiersze (łamią się na
  // strony). Żaden temat ani marka nie wypada z raportu, inaczej PDF pokazywałby mniej niż ekran.
  const presenceColumnWidth = brandPresence?.rows.length
    ? tableContentWidth(PDF_INNER_WIDTH_LANDSCAPE) / brandPresence.rows.length
    : 0;
  // Karta pozioma istnieje tylko wtedy, gdy ma co pokazać — inaczej w PDF zostałaby pusta strona.
  const hasBrandPresence = Boolean(
    brandPresence && brandPresence.topics.length > 0 && brandPresence.rows.length > 0
  );
  const hasPlatformVisibility = models.some((entry) => modelValue(entry) > 0);

  // Lejek liczy procenty z tych samych surowych liczb, co slajd na ekranie (`ReportFunnel`) — jedna
  // definicja `share` po obu stronach, więc PDF nie może pokazać innego ułamka.
  const share = (value: number, base: number): number =>
    base > 0 ? Math.round((value / base) * 1000) / 10 : 0;
  const funnelStages = funnel
    ? [
        {
          key: 'mentioned',
          background: styles.funnelStagePrimary,
          titleColor: {},
          percentColor: {},
          descColor: {},
          title: labels.funnelMentioned,
          percent: share(funnel.mentionedResponses, funnel.totalResponses),
          desc: labels.funnelMentionedDesc(
            labels.numberFormat(funnel.mentionedResponses),
            labels.numberFormat(funnel.totalResponses)
          ),
        },
        {
          key: 'top3',
          background: styles.funnelStageSecondary,
          titleColor: {},
          percentColor: {},
          descColor: {},
          title: labels.funnelTop3,
          percent: share(funnel.top3Responses, funnel.totalResponses),
          desc: labels.funnelTop3Desc(labels.numberFormat(funnel.top3Responses)),
        },
        {
          key: 'top1',
          background: styles.funnelStageBrand,
          titleColor: styles.funnelStageTitleOnBrand,
          percentColor: styles.funnelStagePercentOnBrand,
          descColor: styles.funnelStageDescOnBrand,
          title: labels.funnelTop1,
          percent: share(funnel.top1Responses, funnel.totalResponses),
          desc: labels.funnelTop1Desc(labels.numberFormat(funnel.top1Responses)),
        },
      ]
    : [];
  const funnelSteps = funnel
    ? [
        {
          key: 'toTop3',
          titleColor: styles.funnelStepTitlePrimary,
          title: labels.funnelStepTop3,
          desc: labels.funnelStepTop3Desc(share(funnel.top3Responses, funnel.mentionedResponses)),
        },
        {
          key: 'toTop1',
          titleColor: styles.funnelStepTitleBrand,
          title: labels.funnelStepTop1,
          desc: labels.funnelStepTop1Desc(share(funnel.top1Responses, funnel.mentionedResponses)),
        },
      ]
    : [];

  // Kafel „Your brand": kolor badanej marki bierzemy z tej samej funkcji, co słupki, więc zgadza się
  // z jej pozycją na wykresie.
  const rankedBrands = [...report.brandVisibility].sort((a, b) => brandValue(b) - brandValue(a));
  const trackedIndex = rankedBrands.findIndex((entry) => entry.isTracked);
  const trackedBrand = trackedIndex >= 0 ? rankedBrands[trackedIndex] : undefined;
  // Równa wartość we wszystkich modelach — „dominujący/najsłabszy" sugerowałby różnicę, której nie ma.
  const isPlatformEven = !!weakest && modelValue(dominant) === modelValue(weakest);
  const platformBarWidth = hasPlatformVisibility ? ASIDE_BAR_WIDTH : RANKING_BAR_WIDTH;
  const platformBars = (
    <>
      {models.map((entry) => (
        <View key={entry.model} style={styles.barRow}>
          <Text style={styles.barName}>{modelName(entry.model)}</Text>
          <ReportPdfBar
            value={modelValue(entry)}
            width={platformBarWidth}
            height={PLATFORM_BAR_HEIGHT}
          />
          <Text style={styles.barValue}>{modelValue(entry)}%</Text>
        </View>
      ))}
      <View style={styles.scale}>
        <View style={[styles.scaleTicks, { width: platformBarWidth }]}>
          {SCALE_TICKS.map((tick) => (
            <Text key={tick} style={styles.scaleTick}>
              {tick}%
            </Text>
          ))}
        </View>
      </View>
    </>
  );
  // Ta sama zasada, co na slajdzie (`ReportPromptAnalysis`): dziewięć najlepszych + REALNIE najsłabszy
  // z jego prawdziwym numerem w rankingu. Skrajne prompty liczymy z całej listy, nie z widocznej
  // dziesiątki — inaczej „najniższy wynik" wskazywałby dziesiąty najlepszy.
  // Checker nie dostaje treści promptów (pole opcjonalne) — wtedy sekcja po prostu nie powstaje.
  const sortedPrompts = [...(report.prompts ?? [])].sort((a, b) => b.visibility - a.visibility);
  const rankedPrompts = sortedPrompts.map((prompt, index) => ({ prompt, rank: index + 1 }));
  // Najsłabszy prompt idzie POD listę, za separator — jak na slajdzie ekranowym. Jeden prompt nie
  // ma „najsłabszego": ten sam wiersz nie może stać w obu miejscach.
  const lowestPrompt = rankedPrompts.length > 1 ? rankedPrompts[rankedPrompts.length - 1] : null;
  const listedPrompts = lowestPrompt
    ? rankedPrompts.slice(0, Math.min(PDF_TOP_PROMPTS - 1, rankedPrompts.length - 1))
    : rankedPrompts;
  // Kropki tylko przy realnym przeskoku numeracji; przy kolejnym numerze zostaje sam separator.
  const hasPromptGap =
    !!lowestPrompt && lowestPrompt.rank > listedPrompts[listedPrompts.length - 1].rank + 1;
  // Backend darmowego Checkera nie wysyła treści promptów — wtedy sekcja idzie w wersji zamkniętej.
  const promptsLocked = !report.prompts;
  const hasPromptVisibility = sortedPrompts[0]?.visibility > 0;
  const bestPrompt = hasPromptVisibility ? sortedPrompts[0] : null;
  const worstPrompt =
    hasPromptVisibility && sortedPrompts.length > 1
      ? sortedPrompts[sortedPrompts.length - 1]
      : null;
  // Równa widoczność we wszystkich promptach — „najwyższa/najniższa" sugerowałyby różnicę.
  const isPromptsEven =
    !!bestPrompt && !!worstPrompt && bestPrompt.visibility === worstPrompt.visibility;
  /**
   * Kółko z ikoną trendu przy kaflu skrajnego promptu — odpowiednik `ico--chart-up` / `--chart-down`
   * z panelu. Ścieżka jest przepisana z `ico-activity.svg`; react-pdf nie renderuje mask-image, więc
   * kierunek niesie sam kolor, tak jak w panelu.
   */
  const extremeIcon = (color: string) => (
    // Kółko i ikona w JEDNYM SVG: pozycja liczy się w układzie SVG, a nie flexboksem react-pdf,
    // który przy mieszance View/Text potrafi zignorować `alignItems` i wypchnąć ikonę w róg.
    <View style={[styles.extremeCell, styles.extremeIcon]}>
      <Svg width={26} height={26} viewBox="0 0 26 26">
        <Circle cx={13} cy={13} r={13} fill={PDF_COLORS.white} />
        <G transform="translate(7 7)">
          <Path
            d="M11 6H9L7.5 10.5L4.5 1.5L3 6H1"
            stroke={color}
            strokeWidth={1.5}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </G>
      </Svg>
    </View>
  );

  // Grupa promptów wchodzi na listę jak pojedyncze pytanie — odróżnia ją TYLKO tag i to, że jej
  // nazwa (w przeciwieństwie do promptu) nie jest cytatem, więc nie idzie w cudzysłów.
  const hasPromptGroups = sortedPrompts.some((prompt) => prompt.groupSize !== undefined);
  /**
   * Pigułka „grupa": ikona + tekst, więc jest WIERSZEM, a nie `Text` z tłem. Ikony z panelu są
   * maskami CSS (`mask-image`), których react-pdf nie zna — folder rysujemy tą samą ścieżką, co
   * `ico-folder.svg`, tak jak trzy kropki w `promptLowestGap`.
   */
  const groupTagPill = (text: string, kind: 'list' | 'extreme') => {
    const color = kind === 'list' ? PDF_COLORS.secondary600 : PDF_COLORS.grey600;
    return (
      <View style={kind === 'list' ? styles.promptGroupTag : styles.extremeGroupTag}>
        <Svg width={GROUP_TAG_ICON} height={GROUP_TAG_ICON} viewBox="0 0 18 18">
          <Path
            d={FOLDER_PATH}
            stroke={color}
            strokeWidth={1.5}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </Svg>
        <Text style={kind === 'list' ? styles.promptGroupTagText : styles.extremeGroupTagText}>
          {text}
        </Text>
      </View>
    );
  };
  const promptRowLabel = (entry: (typeof sortedPrompts)[number]) =>
    entry.groupSize === undefined ? (
      <Text style={styles.promptText}>{entry.prompt}</Text>
    ) : (
      <View style={styles.promptLabelRow}>
        {groupTagPill(labels.promptsGroupTag(entry.groupSize), 'list')}
        <Text style={[styles.promptText, { marginBottom: 0 }]}>{entry.prompt}</Text>
      </View>
    );
  // Tag idzie NAD nazwę grupy, w osobną linię — etykieta kafla mieści się w jednym wierszu, a nazwa
  // dostaje pod tagiem pełną szerokość na zawinięcie (tak samo jak na ekranie).
  const extremeBody = (entry: (typeof sortedPrompts)[number]) =>
    entry.groupSize === undefined ? (
      <Text style={styles.extremePrompt}>&quot;{entry.prompt}&quot;</Text>
    ) : (
      <View>
        {/* Pigułka siedzi w WIERSZU, choć jest sama: w kolumnie react-pdf ignoruje `alignSelf` na
            dziecku (patrz `extremeCell`) i tło rozlewa się na całą szerokość kafla. */}
        <View style={styles.extremeGroupTagRow}>
          {groupTagPill(labels.promptsGroupTagCount(entry.groupSize), 'extreme')}
        </View>
        <Text style={styles.extremePrompt}>{entry.prompt}</Text>
      </View>
    );

  const promptList = (
    <>
      {listedPrompts.map(({ prompt, rank }) => (
        <View key={prompt.prompt} style={styles.promptRow}>
          <Text style={styles.promptRank}>{rank}</Text>
          <View style={styles.promptMain}>
            {promptRowLabel(prompt)}
            <ReportPdfBar
              value={prompt.visibility}
              width={bestPrompt ? PROMPT_BAR_WIDTH : RANKING_BAR_WIDTH}
              height={PROMPT_BAR_HEIGHT}
            />
          </View>
          <Text style={styles.barValue}>{prompt.visibility}%</Text>
        </View>
      ))}

      {lowestPrompt && (
        <View style={styles.promptLowest}>
          <View style={styles.promptLowestLabel}>
            {hasPromptGap && (
              // Odpowiednik ikony `ico--more` z panelu — react-pdf nie zna klas, więc te same trzy
              // kropki rysujemy wprost, zamiast wstawiać znak „•••" zależny od kroju pisma.
              <View style={styles.promptLowestGap}>
                <Svg width={12} height={4} viewBox="0 0 12 4">
                  <Circle cx={2} cy={2} r={1.2} fill={PDF_COLORS.grey600} />
                  <Circle cx={6} cy={2} r={1.2} fill={PDF_COLORS.grey600} />
                  <Circle cx={10} cy={2} r={1.2} fill={PDF_COLORS.grey600} />
                </Svg>
              </View>
            )}
            <Text style={styles.promptLowestText}>{labels.promptsLowest}</Text>
          </View>
          <View style={[styles.promptRow, styles.promptLowestRow]}>
            <Text style={styles.promptRank}>{lowestPrompt.rank}</Text>
            <View style={styles.promptMain}>
              {promptRowLabel(lowestPrompt.prompt)}
              <ReportPdfBar
                value={lowestPrompt.prompt.visibility}
                width={bestPrompt ? PROMPT_BAR_WIDTH : RANKING_BAR_WIDTH}
                height={PROMPT_BAR_HEIGHT}
              />
            </View>
            <Text style={styles.barValue}>{lowestPrompt.prompt.visibility}%</Text>
          </View>
        </View>
      )}
    </>
  );

  return (
    <Document title={`${brandLabel(report.brand)} — ${labels.coverSubtitle}`}>
      <Page size="A4" style={styles.page}>
        {/* Poświata pod okładką — odpowiednik gradientu tła strony. */}
        <Svg width={PDF_PAGE.width} height={PDF_GRADIENT_HEIGHT} style={styles.gradient} fixed={false}>
          <Defs>
            <LinearGradient id="page-glow" x1="0" y1="0" x2="0" y2="1">
              <Stop offset="0" stopColor={PDF_COLORS.primary100} />
              <Stop offset="1" stopColor={PDF_COLORS.grey10} />
            </LinearGradient>
          </Defs>
          <Rect x={0} y={0} width={PDF_PAGE.width} height={PDF_GRADIENT_HEIGHT} fill="url(#page-glow)" />
        </Svg>

        <View style={styles.cover}>
          <Image src={chatbeatLogoFull} style={styles.logo} />
          <Text style={styles.coverBrand}>{spacedCaps(brandLabel(report.brand))}</Text>
          <Text style={styles.coverTitle}>{labels.coverSubtitle}</Text>
          <View style={styles.coverRule} />
          <Text style={styles.coverDate}>{labels.coverDate}</Text>

          <View style={styles.coverStats}>
            <View style={styles.coverStat}>
              <View>
                <Text style={styles.coverStatValue}>{formatCompactCount(report.promptsAnalyzed)}</Text>
                <Text style={styles.coverStatLabel}>{labels.prompts}</Text>
              </View>
            </View>
            {!!responsesAnalyzed && (
              <View style={styles.coverStat}>
                <View>
                  <Text style={styles.coverStatValue}>{formatCompactCount(responsesAnalyzed)}</Text>
                  <Text style={styles.coverStatLabel}>{labels.allResponses}</Text>
                </View>
              </View>
            )}
            <View style={styles.coverStat}>
              <View>
                <Text style={styles.coverStatValue}>{formatCompactCount(models.length)}</Text>
                <Text style={styles.coverStatLabel}>{labels.models}</Text>
              </View>
              <View style={styles.coverStatIcons}>
                {/* Kanoniczna kolejność z backendu, tak jak na okładce ekranowej. `models` jest
                    posortowane malejąco po widoczności — to porządek slajdu Platform Performance,
                    nie okładki, i na kaflu wychodziła z tego inna kolejność ikon niż w podglądzie. */}
                {report.visibilityPerModel.map((entry) => {
                  const shape = MODEL_ICON_SHAPES[entry.model];
                  if (!shape) {
                    return null;
                  }
                  // Ikony z gradientem (Gemini) muszą go dostać naprawdę — jednolity pierwszy
                  // przystanek daje inny kolor niż logo w panelu.
                  const gradientId = `model-${entry.model}`;
                  return (
                    <Svg key={entry.model} width={14} height={14} viewBox={shape.viewBox}>
                      {shape.gradient && (
                        <Defs>
                          {/* Wartości LICZBOWE w układzie viewBox (24×24), nie procenty — procentów
                              react-pdf nie parsuje i cały render leci na „unsupported number: NaN".
                              Środek w lewym dolnym rogu znaku, tak jak w źródłowym `ico-gemini-bck`. */}
                          <RadialGradient id={gradientId} cx="5" cy="20" r="26" fx="5" fy="20">
                            {shape.gradient.stops.map((stop) => (
                              <Stop
                                key={stop.offset}
                                offset={String(stop.offset)}
                                stopColor={stop.color}
                              />
                            ))}
                          </RadialGradient>
                        </Defs>
                      )}
                      {shape.paths.map((path, index) => (
                        <Path
                          key={index}
                          d={path.d}
                          fill={shape.gradient ? `url(#${gradientId})` : path.fill}
                        />
                      ))}
                    </Svg>
                  );
                })}
              </View>
            </View>
          </View>
        </View>

        <ReportPdfSection
          title={labels.summaryTitle}
          titleAccent={labels.summaryTitleAccent}
          subtitle={labels.summarySubtitle}
        >
          <View style={styles.tiles}>
            {[
              { label: labels.visibility, value: labels.visibilityValue },
              { label: labels.shareOfVoice, value: `${report.score.shareOfVoice}%` },
              { label: labels.brandScore, value: `${Math.round(report.score.brandScore)}%` },
              { label: labels.positionLabel, value: labels.positionValue },
            ].map((tile) => (
              <View key={tile.label} style={styles.tile}>
                <Text style={styles.tileValue}>{tile.value}</Text>
                <Text style={styles.tileLabel}>{tile.label}</Text>
              </View>
            ))}
          </View>
        </ReportPdfSection>

        {trendChart && (
          <ReportPdfSection
            title={labels.trendTitle}
            titleAccent={labels.trendTitleAccent}
            subtitle={labels.trendSubtitle}
          >
            <View style={styles.chartLegend}>
              {labels.trendLegend.map((entry) => (
                <View key={entry.label} style={styles.chartLegendItem}>
                  <View style={[styles.chartLegendDot, { backgroundColor: entry.color }]} />
                  <Text style={styles.chartLegendLabel}>{entry.label}</Text>
                </View>
              ))}
            </View>
            <Image src={trendChart} style={styles.chartImage} />
          </ReportPdfSection>
        )}

        {hasPlatformVisibility ? (
          <ReportPdfSection
            title={labels.platformTitle}
            titleAccent={labels.platformTitleAccent}
            aside={
              <ReportPdfPanel title={labels.platformMetricsTitle} desc={labels.platformMetricsDesc}>
                {platformBars}
              </ReportPdfPanel>
            }
          >
            {isPlatformEven ? (
              <View style={[styles.edgeBox, styles.edgeBoxTop]}>
                <Text style={styles.edgeLabel}>{labels.platformEven}</Text>
                <Text style={styles.edgeName}>{modelValue(dominant)}%</Text>
                <Text style={[styles.edgeShare, { color: PDF_COLORS.secondary }]}>
                  {labels.platformEvenDesc(models.length)}
                </Text>
              </View>
            ) : (
              <>
                {dominant && (
                  <View style={[styles.edgeBox, styles.edgeBoxTop]}>
                    <Text style={styles.edgeLabel}>{labels.platformDominant}</Text>
                    <Text style={styles.edgeName}>{modelName(dominant.model)}</Text>
                    <Text style={[styles.edgeShare, { color: PDF_COLORS.secondary }]}>
                      {labels.platformShare(modelValue(dominant))}
                    </Text>
                  </View>
                )}
                {weakest && (
                  <View style={[styles.edgeBox, { marginTop: 14 }]}>
                    <Text style={styles.edgeLabel}>{labels.platformWeakest}</Text>
                    <Text style={styles.edgeName}>{modelName(weakest.model)}</Text>
                    <Text style={styles.edgeShare}>{labels.platformShare(modelValue(weakest))}</Text>
                  </View>
                )}
              </>
            )}
          </ReportPdfSection>
        ) : (
          // Same zera — kafle „dominujący/najsłabszy" nic nie wnoszą, paski biorą całą sekcję.
          <ReportPdfSection title={labels.platformTitle} titleAccent={labels.platformTitleAccent}>
            <ReportPdfPanelHeading
              title={labels.platformMetricsTitle}
              desc={labels.platformMetricsDesc}
            />
            {platformBars}
          </ReportPdfSection>
        )}

        {report.brandVisibility.length > 0 && (
          <ReportPdfSection
            title={labels.rankingTitle}
            titleAccent={labels.rankingTitleAccent}
            subtitle={labels.rankingSubtitle}
          >
            {trackedBrand && (
              <View
                style={[
                  styles.rankingBrand,
                  { backgroundColor: brandColor(trackedIndex, true) },
                ]}
              >
                <Text style={styles.rankingBrandLabel}>{labels.rankingYourBrand}</Text>
                <View style={styles.rankingBrandValue}>
                  <Text style={styles.rankingBrandName}>
                    {truncate(brandLabel(trackedBrand.brand), RANKING_BRAND_CHARS)}
                  </Text>
                  <Text style={styles.rankingBrandPercent}>{brandValue(trackedBrand)}%</Text>
                </View>
              </View>
            )}
            {rankingChart ? (
              <Image src={rankingChart} style={styles.chartImage} />
            ) : (
              report.brandVisibility.slice(0, 7).map((entry, index) => (
                <View key={entry.brand} style={styles.barRow}>
                  <Text style={styles.barName}>{brandLabel(entry.brand)}</Text>
                  <ReportPdfBar
                    value={(brandValue(entry) / maxBrandVisibility) * 100}
                    width={RANKING_BAR_WIDTH}
                    solid={brandColor(index, entry.isTracked)}
                  />
                  <Text style={styles.barValue}>{brandValue(entry)}%</Text>
                </View>
              ))
            )}
          </ReportPdfSection>
        )}

        {funnel && funnel.totalResponses > 0 && (
          <ReportPdfSection
            title={labels.funnelTitle}
            titleAccent={labels.funnelTitleAccent}
            subtitle={labels.funnelSubtitle}
          >
            <View style={styles.funnelStages}>
              {funnelStages.map((stage) => (
                <View key={stage.key} style={[styles.funnelStage, stage.background]}>
                  <Text style={[styles.funnelStageTitle, stage.titleColor]}>{stage.title}</Text>
                  <Text style={[styles.funnelStagePercent, stage.percentColor]}>{stage.percent}%</Text>
                  <Text style={[styles.funnelStageDesc, stage.descColor]}>{stage.desc}</Text>
                </View>
              ))}
            </View>
            <View style={styles.funnelSteps}>
              {funnelSteps.map((step) => (
                <View key={step.key} style={styles.funnelStep}>
                  <Text style={[styles.funnelStepTitle, step.titleColor]}>{step.title}</Text>
                  <Text style={styles.funnelStepDesc}>{step.desc}</Text>
                </View>
              ))}
            </View>
          </ReportPdfSection>
        )}

        <ReportPdfSection
          title={labels.sovTitle}
          titleAccent={labels.sovTitleAccent}
          subtitle={labels.sovSubtitle}
          aside={
            <ReportPdfPanel title={labels.sovChartTitle} desc={labels.sovChartDesc}>
              {sovChart ? (
                <Image src={sovChart} style={styles.chartImage} />
              ) : (
                report.shareOfVoice.slice(0, 7).map((entry, index) => (
                  <View key={entry.brand} style={styles.barRow}>
                    <Text style={styles.barName}>{brandLabel(entry.brand)}</Text>
                    <ReportPdfBar
                      value={entry.shareOfVoice}
                      width={ASIDE_BAR_WIDTH}
                      solid={brandColor(index, entry.isTracked)}
                    />
                    <Text style={styles.barValue}>{entry.shareOfVoice}%</Text>
                  </View>
                ))
              )}
            </ReportPdfPanel>
          }
        >
          {report.shareOfVoice.slice(0, 7).map((entry, index) => (
            <View
              key={entry.brand}
              style={[styles.rankRow, entry.isTracked ? styles.rankRowTracked : {}]}
            >
              <View style={[styles.rankDot, { backgroundColor: brandColor(index, entry.isTracked) }]} />
              <Text style={styles.rankLabel}>{brandLabel(entry.brand)}</Text>
              <Text style={styles.rankValue}>{entry.shareOfVoice}%</Text>
            </View>
          ))}
        </ReportPdfSection>

        {report.competitorMatrix.models.length > 0 && (
          <ReportPdfSection
            title={labels.matrixTitle}
            titleAccent={labels.matrixTitleAccent}
            subtitle={labels.matrixSubtitle}
          >
            <View style={styles.table}>
              <View style={styles.tableRow}>
                <Text style={[styles.tableHead, { width: TABLE_LABEL_WIDTH, textAlign: 'left' }]}>
                  {labels.brand}
                </Text>
                {report.competitorMatrix.models.map((model) => (
                  <Text key={model} style={[styles.tableHead, { width: matrixColumnWidth }]}>
                    {modelName(model)}
                  </Text>
                ))}
              </View>
              {report.competitorMatrix.rows.map((row, rowIndex) => (
                <View
                  key={row.brand}
                  style={[styles.tableRow, row.isTracked ? styles.tableRowTracked : {}]}
                >
                  <View style={styles.tableBrandCell}>
                    <Text
                      style={[
                        styles.brandBadge,
                        { backgroundColor: brandColor(rowIndex, row.isTracked) },
                      ]}
                    >
                      {brandLabel(row.brand).charAt(0).toUpperCase()}
                    </Text>
                    <Text
                      style={[styles.tableBrand, row.isTracked ? { color: PDF_COLORS.primary600 } : {}]}
                    >
                      {brandLabel(row.brand)}
                    </Text>
                  </View>
                  {row.values.map((value, index) => (
                    <View
                      key={`${row.brand}-${index}`}
                      style={[styles.tableCellWrap, { width: matrixColumnWidth }]}
                    >
                      <Text style={[styles.tableCell, cellStyle(value)]}>
                        {value === null ? '-' : `${value}%`}
                      </Text>
                    </View>
                  ))}
                </View>
              ))}
            </View>
          </ReportPdfSection>
        )}

        {topicShareChart && topicShares.length > 0 && (
          <ReportPdfSection
            title={labels.topicsTitle}
            titleAccent={labels.topicsTitleAccent}
            subtitle={labels.topicsSubtitle}
          >
            <Image src={topicShareChart} style={styles.chartImage} />
          </ReportPdfSection>
        )}
      </Page>

      {/* Tabele tematów POZIOMO, każda na własnej karcie: mają tyle kolumn, że na stronie pionowej
          nie mieszczą się bez ściskania, a obie na jednej karcie i tak schodzą na dwie strony
          (zmierzone) — więc rozdzielone kosztują tyle samo, a łamanie nie wypada w środku tabeli.
          Reszta raportu zostaje pionowa. */}
      {hasBrandPresence && brandPresence && (
        <Page size="A4" orientation="landscape" style={styles.page}>
          <ReportPdfSection
            title={labels.presenceTitle}
            titleAccent={labels.presenceTitleAccent}
            subtitle={labels.presenceSubtitle}
            breakable
          >
            <View style={styles.table}>
              <View style={styles.tableRow}>
                <Text style={[styles.tableHead, { width: TABLE_LABEL_WIDTH, textAlign: 'left' }]}>
                  {labels.gapsTopic}
                </Text>
                {brandPresence.rows.map((row) => (
                  <Text
                    key={row.brand}
                    style={[
                      styles.tableHead,
                      { width: presenceColumnWidth },
                      row.isTracked ? styles.tableHeadTracked : {},
                    ]}
                  >
                    {truncate(brandLabel(row.brand), PRESENCE_HEAD_CHARS)}
                  </Text>
                ))}
              </View>
              {brandPresence.topics.map((topic, topicIndex) => (
                <View key={topic} style={styles.tableRow}>
                  <Text style={[styles.tableCell, { width: TABLE_LABEL_WIDTH, textAlign: 'left' }]}>
                    {topic}
                  </Text>
                  {brandPresence.rows.map((row) => {
                    const value = row.values[topicIndex] ?? null;

                    return (
                      <View
                        key={`${topic}-${row.brand}`}
                        style={[
                          styles.tableCellWrap,
                          { width: presenceColumnWidth },
                          // Marka projektu wyróżniona kolumną — na stronie wyróżniony jest wiersz,
                          // a tabela jest tu obrócona.
                          row.isTracked ? styles.tableCellTracked : {},
                        ]}
                      >
                        <Text style={[styles.tableCell, cellStyle(value)]}>
                          {value === null ? '-' : `${value}%`}
                        </Text>
                      </View>
                    );
                  })}
                </View>
              ))}
            </View>
          </ReportPdfSection>
        </Page>
      )}

      {contextGaps.length > 0 && (
        <Page size="A4" orientation="landscape" style={styles.page}>
          <ReportPdfSection
            title={labels.gapsTitle}
            titleAccent={labels.gapsTitleAccent}
            subtitle={labels.gapsSubtitle}
            breakable
          >
            <View style={styles.table}>
              <View style={styles.tableRow}>
                <Text style={[styles.tableHead, { width: TABLE_LABEL_WIDTH, textAlign: 'left' }]}>
                  {labels.gapsTopic}
                </Text>
                <Text style={[styles.tableHead, { flex: 1, textAlign: 'left' }]}>
                  {labels.gapsBrands}
                </Text>
                <Text style={[styles.tableHead, { width: 60, textAlign: 'right' }]}>
                  {labels.gapsOccurrences}
                </Text>
              </View>
              {contextGaps.map((gap) => (
                <View key={gap.topic} style={[styles.tableRow, styles.tableRowTop]}>
                  <Text style={[styles.tableBrand, { width: TABLE_LABEL_WIDTH }]}>
                    {truncate(gap.topic, CARD_TITLE_CHARS)}
                  </Text>
                  <View style={[styles.tags, { flex: 1 }]}>
                    {gap.topBrands.slice(0, GAP_VISIBLE_BRANDS).map((brand) => (
                      <Text key={brand} style={styles.tag}>
                        {truncate(brand, 18)}
                      </Text>
                    ))}
                    {gap.topBrands.length > GAP_VISIBLE_BRANDS && (
                      <Text style={[styles.tag, styles.tagMore]}>
                        +{gap.topBrands.length - GAP_VISIBLE_BRANDS}
                      </Text>
                    )}
                  </View>
                  <Text style={[styles.tableBrand, { width: 60, textAlign: 'right' }]}>
                    {gap.occurrences}
                  </Text>
                </View>
              ))}
            </View>
          </ReportPdfSection>
        </Page>
      )}

      <Page size="A4" style={styles.page}>
        <ReportPdfSection
          title={labels.domainsTitle}
          titleAccent={labels.domainsTitleAccent}
          subtitle={labels.domainsSubtitle}
        >
          <View style={styles.grid}>
            {report.sources.topDomains.map((entry, index) => (
              <View
                key={entry.domain}
                style={[
                  styles.card,
                  entry.isTrackedDomain ? styles.cardTracked : {},
                  (index + 1) % 3 === 0 ? styles.cardLast : {},
                ]}
              >
                <Text style={styles.cardRank}>{index + 1}</Text>
                <View style={styles.cardMain}>
                  <Link src={`https://${entry.domain}`} style={styles.cardNameLink}>
                    {truncate(entry.domain, CARD_TITLE_CHARS)}
                  </Link>
                  <Text style={styles.cardMeta}>{labels.domainsShare(entry.share)}</Text>
                  <ReportPdfBar
                    value={entry.share}
                    width={70}
                    height={CARD_BAR_HEIGHT}
                    solid={entry.isTrackedDomain ? PDF_COLORS.secondary : PDF_COLORS.primary}
                  />
                </View>
                <Text style={styles.cardCitations}>{entry.citations}</Text>
              </View>
            ))}
          </View>
        </ReportPdfSection>

        {report.sources.topPages.length > 0 && (
          <ReportPdfSection
            title={labels.pagesTitle}
            titleAccent={labels.pagesTitleAccent}
            subtitle={labels.pagesSubtitle}
          >
            <View style={styles.grid}>
              {report.sources.topPages.map((page, index) => (
                <View
                  key={page.url}
                  style={[
                    styles.card,
                    page.isTrackedDomain ? styles.cardTracked : {},
                    (index + 1) % 3 === 0 ? styles.cardLast : {},
                  ]}
                >
                  <Text style={styles.cardRank}>{index + 1}</Text>
                  <View style={styles.cardMain}>
                    <Link src={page.url} style={styles.cardNameLink}>
                      {truncate(page.title ?? page.domain, CARD_TITLE_CHARS)}
                    </Link>
                    <Link src={page.url} style={styles.cardMetaLink}>
                      {shortUrl(page.url)}
                    </Link>
                    <ReportPdfBar
                      value={page.share}
                      width={70}
                      height={CARD_BAR_HEIGHT}
                      solid={page.isTrackedDomain ? PDF_COLORS.secondary : PDF_COLORS.primary}
                    />
                  </View>
                  <Text style={styles.cardCitations}>{page.citations}</Text>
                </View>
              ))}
            </View>
          </ReportPdfSection>
        )}

        {promptsLocked ? (
          <ReportPdfSection
            title={labels.promptsTitle}
            titleAccent={labels.promptsTitleAccent}
          >
            {/* Szkielet: same paski, zero treści. Odpowiednik rozmytej atrapy ze strony. */}
            {LOCKED_TEASER_WIDTHS.map((width, index) => (
              <View key={width} style={styles.lockedRow}>
                <Text style={styles.promptRank}>{index + 1}</Text>
                <View style={styles.promptMain}>
                  <View style={[styles.lockedTextBar, { width: `${width}%` }]} />
                  <ReportPdfBar value={width} width={RANKING_BAR_WIDTH} />
                </View>
                <Text style={styles.barValue}>{width}%</Text>
              </View>
            ))}

            <View style={styles.lockedCta}>
              <Text style={styles.lockedCtaTitle}>{labels.promptsLockedTitle}</Text>
              <Text style={styles.lockedCtaDesc}>{labels.promptsLockedDesc}</Text>
              <Link src={labels.signUpUrl} style={styles.lockedCtaLink}>
                {labels.promptsLockedCta}
              </Link>
            </View>
          </ReportPdfSection>
        ) : bestPrompt && worstPrompt ? (
          <ReportPdfSection
            title={labels.promptsTitle}
            titleAccent={labels.promptsTitleAccent}
            subtitle={labels.promptsSubtitle}
            aside={
              <ReportPdfPanel
                title={hasPromptGroups ? labels.promptsTopTitleGroups : labels.promptsTopTitle}
                desc={hasPromptGroups ? labels.promptsTopDescGroups : labels.promptsTopDesc}
              >
                {promptList}
              </ReportPdfPanel>
            }
          >
            {isPromptsEven ? (
              <View style={[styles.extreme, styles.extremeBest]}>
                {extremeIcon(PDF_COLORS.secondary600)}
                <View style={styles.extremeContent}>
                  <Text style={[styles.extremeLabel, { color: PDF_COLORS.secondary }]}>
                    {labels.promptsEven}
                  </Text>
                  <Text style={styles.extremePrompt}>
                    {labels.promptsEvenDesc(sortedPrompts.length)}
                  </Text>
                </View>
                <View style={styles.extremeCell}>
                  <Text style={styles.extremeValue}>{bestPrompt.visibility}%</Text>
                </View>
              </View>
            ) : (
              <>
                <View style={[styles.extreme, styles.extremeBest]}>
                  {extremeIcon(PDF_COLORS.secondary600)}
                  <View style={styles.extremeContent}>
                    <Text style={[styles.extremeLabel, { color: PDF_COLORS.secondary }]}>
                      {labels.promptsBest}
                    </Text>
                    {extremeBody(bestPrompt)}
                  </View>
                  <View style={styles.extremeCell}>
                    <Text style={styles.extremeValue}>{bestPrompt.visibility}%</Text>
                  </View>
                </View>
                <View style={[styles.extreme, styles.extremeWorst]}>
                  {extremeIcon(PDF_COLORS.grey600)}
                  <View style={styles.extremeContent}>
                    <Text style={[styles.extremeLabel, { color: PDF_COLORS.grey600 }]}>
                      {labels.promptsWorst}
                    </Text>
                    {extremeBody(worstPrompt)}
                  </View>
                  <View style={styles.extremeCell}>
                    <Text style={styles.extremeValue}>{worstPrompt.visibility}%</Text>
                  </View>
                </View>
              </>
            )}
          </ReportPdfSection>
        ) : (
          <ReportPdfSection
            title={labels.promptsTitle}
            titleAccent={labels.promptsTitleAccent}
            subtitle={labels.promptsSubtitle}
          >
            <ReportPdfPanelHeading
              title={hasPromptGroups ? labels.promptsTopTitleGroups : labels.promptsTopTitle}
              desc={hasPromptGroups ? labels.promptsTopDescGroups : labels.promptsTopDesc}
            />
            {promptList}
          </ReportPdfSection>
        )}
      </Page>
    </Document>
  );
};

export default ReportPdfDocument;
